import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition bg-white";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) navigate("/admin");
    else setError(result.error);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <Seo title="Área Administrativa | Casa Vale" description="Acesso restrito à equipe Casa Vale." />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 sm:p-10"
      >
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <h1 className="text-xl font-bold text-slate-900 text-center" data-testid="admin-login-title">
          Área Administrativa
        </h1>
        <p className="text-sm text-slate-500 text-center mt-1 mb-8">Acesso restrito à equipe Casa Vale.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail"
              className={`${inputCls} pl-11`}
              autoComplete="email"
              data-testid="admin-email-input"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              className={`${inputCls} pl-11`}
              autoComplete="current-password"
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5" role="alert" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold py-3 rounded-full transition-colors duration-200"
            data-testid="admin-login-submit"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-site-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao site
        </Link>
      </motion.div>
    </div>
  );
}
